/**
 * Authentication Context
 * Manages Firebase Auth state from Core SIS
 * Provides user info, role, and auth methods to entire app
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, coreSISDb, examDb } from '../config/firebase';
import { CORE_SIS_COLLECTIONS, ROLES, COLLECTIONS } from '../config/constants';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile and role from Core SIS users collection
  const fetchUserProfile = useCallback(async (firebaseUser) => {
    try {
      // Try to get role from custom claims first
      const tokenResult = await firebaseUser.getIdTokenResult();
      let role = tokenResult.claims.role;

      if (!role) {
        const userDocRef = doc(coreSISDb, CORE_SIS_COLLECTIONS.USERS, firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          role = userData.role || ROLES.TEACHER; // Default to teacher
          
          let resolvedTeacherId = userData.teacherId || null;
          let examTeacherProfile = null;
          
          // If this is a teacher, try to find their ID in examTeachers by email or name
          if (role === ROLES.TEACHER) {
            try {
              if (resolvedTeacherId) {
                  const docRef = doc(examDb, COLLECTIONS.EXAM_TEACHERS, resolvedTeacherId);
                  const docSnap = await getDoc(docRef);
                  if (docSnap.exists()) {
                      examTeacherProfile = docSnap.data();
                  }
              }

              if (!examTeacherProfile) {
                // Try by email first
                const tq = query(collection(examDb, COLLECTIONS.EXAM_TEACHERS), where('email', '==', firebaseUser.email));
                const ts = await getDocs(tq);
                if (!ts.empty) {
                  resolvedTeacherId = ts.docs[0].id;
                  examTeacherProfile = ts.docs[0].data();
                } else if (userData.displayName) {
                  // Fallback by displayName
                  const tq2 = query(collection(examDb, COLLECTIONS.EXAM_TEACHERS), where('teacherName', '==', userData.displayName));
                  const ts2 = await getDocs(tq2);
                  if (!ts2.empty) {
                    resolvedTeacherId = ts2.docs[0].id;
                    examTeacherProfile = ts2.docs[0].data();
                  }
                }
              }
            } catch (error) {
              console.error('Error resolving teacher ID:', error);
            }
          }

          // Fetch Teacher Profile from Core SIS if it's a teacher
          let coreTeacherProfile = null;
          if (resolvedTeacherId || userData.teacherId) {
             const coreTeacherId = userData.teacherId || resolvedTeacherId;
             try {
                const coreTeacherRef = doc(coreSISDb, CORE_SIS_COLLECTIONS.TEACHERS, coreTeacherId);
                const coreTeacherSnap = await getDoc(coreTeacherRef);
                if (coreTeacherSnap.exists()) {
                   coreTeacherProfile = coreTeacherSnap.data();
                } else {
                   const tQuery = query(collection(coreSISDb, CORE_SIS_COLLECTIONS.TEACHERS), where('teacherId', '==', coreTeacherId));
                   const tSnap = await getDocs(tQuery);
                   if (!tSnap.empty) {
                      coreTeacherProfile = tSnap.docs[0].data();
                   } else if (firebaseUser.email) {
                      const emailQuery = query(collection(coreSISDb, CORE_SIS_COLLECTIONS.TEACHERS), where('email', '==', firebaseUser.email));
                      const emailSnap = await getDocs(emailQuery);
                      if (!emailSnap.empty) {
                         coreTeacherProfile = emailSnap.docs[0].data();
                      }
                   }
                }
             } catch (err) {
                console.error('Error fetching core teacher profile:', err);
             }
          }

          return {
            uid: firebaseUser.uid,
            teacherId: resolvedTeacherId,
            coreTeacherId: userData.teacherId,
            email: firebaseUser.email,
            role: role,
            isSuperAdmin: role === ROLES.SUPER_ADMIN,
            isAdmin: role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN,
            isTeacher: role === ROLES.TEACHER,
            phone: userData.phone || '',
            ...userData,
            displayName: userData.displayName || userData.name || firebaseUser.email,
            coreProfile: coreTeacherProfile,
            examProfile: examTeacherProfile,
          };
        }
      }

      // If no custom claims and no user doc, default response
      let fallbackTeacherId = null;
      if (!fallbackTeacherId && (role || ROLES.TEACHER) === ROLES.TEACHER) {
        try {
          const tq = query(collection(examDb, COLLECTIONS.EXAM_TEACHERS), where('email', '==', firebaseUser.email));
          const ts = await getDocs(tq);
          if (!ts.empty) fallbackTeacherId = ts.docs[0].id;
        } catch (e) {}
      }

      return {
        uid: firebaseUser.uid,
        teacherId: fallbackTeacherId,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || firebaseUser.email,
        role: role || ROLES.TEACHER,
        isSuperAdmin: (role || ROLES.TEACHER) === ROLES.SUPER_ADMIN,
        isAdmin: (role || ROLES.TEACHER) === ROLES.ADMIN || (role || ROLES.TEACHER) === ROLES.SUPER_ADMIN,
        isTeacher: (role || ROLES.TEACHER) === ROLES.TEACHER,
        phone: firebaseUser.phoneNumber || '',
      };
    } catch (err) {
      console.error('Error fetching user profile:', err);
      // Return basic profile even if Core SIS read fails
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || firebaseUser.email,
        role: ROLES.TEACHER,
        isSuperAdmin: false,
        isAdmin: false,
        isTeacher: true,
        phone: '',
      };
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const profile = await fetchUserProfile(firebaseUser);
        
        if (profile.isActive === false) {
          await firebaseSignOut(auth);
          setUser(null);
          setUserProfile(null);
          setError('আপনি আপাতত কিছুক্ষণ আমাদের সিস্টেমে প্রবেশ করতে পারবেন না। নিরাপত্তার কারণে আমরা আপনার লগিন ব্লক করে রেখেছি। কিছুক্ষণ পর আপনাকে আনব্লক করা হবে। আপনার যদি ইমার্জেন্সি সিস্টেমে প্রবেশ করতে হয়, তাহলে সুপার এডমিন জনাব, আছআদ সিরাজীর সাথে যোগাযোগ করুন। ধন্যবাদ।');
        } else {
          setUser(firebaseUser);
          setUserProfile(profile);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  const signIn = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Check isActive from Core SIS
      const userDocRef = doc(coreSISDb, CORE_SIS_COLLECTIONS.USERS, result.user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.isActive === false) {
          await firebaseSignOut(auth);
          const error = new Error('আপনি আপাতত কিছুক্ষণ আমাদের সিস্টেমে প্রবেশ করতে পারবেন না। নিরাপত্তার কারণে আমরা আপনার লগিন ব্লক করে রেখেছি। কিছুক্ষণ পর আপনাকে আনব্লক করা হবে। আপনার যদি ইমার্জেন্সি সিস্টেমে প্রবেশ করতে হয়, তাহলে সুপার এডমিন জনাব, আছআদ সিরাজীর সাথে যোগাযোগ করুন। ধন্যবাদ।');
          error.code = 'auth/user-disabled-custom';
          throw error;
        }
      }
      
      return result;
    } catch (err) {
      let message = 'Login failed. Please check your credentials.';
      if (err.code === 'auth/user-not-found') message = 'No account found with this email.';
      else if (err.code === 'auth/wrong-password') message = 'Incorrect password.';
      else if (err.code === 'auth/too-many-requests') message = 'Too many attempts. Please try again later.';
      else if (err.code === 'auth/invalid-credential') message = 'Invalid credentials. Please try again.';
      else if (err.code === 'auth/user-disabled-custom' || err.message === 'Your account has been deactivated. Please contact the administrator.' || err.message.includes('আপাতত কিছুক্ষণ')) {
        message = 'আপনি আপাতত কিছুক্ষণ আমাদের সিস্টেমে প্রবেশ করতে পারবেন না। নিরাপত্তার কারণে আমরা আপনার লগিন ব্লক করে রেখেছি। কিছুক্ষণ পর আপনাকে আনব্লক করা হবে। আপনার যদি ইমার্জেন্সি সিস্টেমে প্রবেশ করতে হয়, তাহলে সুপার এডমিন জনাব, আছআদ সিরাজীর সাথে যোগাযোগ করুন। ধন্যবাদ।';
      }
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // Permission helpers
  const isSuperAdmin = userProfile?.role === ROLES.SUPER_ADMIN;
  const isAdmin = userProfile?.role === ROLES.ADMIN || isSuperAdmin;
  const isTeacher = userProfile?.role === ROLES.TEACHER;

  const hasRole = (requiredRole) => {
    if (!userProfile) return false;
    if (userProfile.role === ROLES.SUPER_ADMIN) return true;
    if (requiredRole === ROLES.ADMIN) return isAdmin;
    if (requiredRole === ROLES.TEACHER) return true;
    return userProfile.role === requiredRole;
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    signIn,
    signOut,
    isSuperAdmin,
    isAdmin,
    isTeacher,
    hasRole,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
